import axios from "@/utils/axios";

enum Api {
  index = "/wework/index",
  menuList = "/wework/index/menuList",
  info = "/wework/login/getInfo",
  attendanceDateList = "/wework/attendance/dateList",
  attendanceRecord = "/wework/attendance/record",
  // doorDeviceList = "/wework/door/deviceList",
  doorDeviceList = "/wework/door/doorList",
  doorOpen = "/wework/door/openDoor",
  doorOpenList = "/wework/door/openList",
  // 访客管理（沿用小程序端 logistics-mini 的接口）
  visitorList = "/wework/visitor/list",
  visitorInfo = "/wework/visitor/info",
  visitorCheck = "/wework/visitor/check",
  visitorSave = "/wework/visitor/save",
  visitDoorList = "/wework/door/doorList",
  // 访客通过邀请链接填写（无需登录态）
  openAddVisitor = "/wework/visitor/open/save",
}

export interface PersonInfo {
  personId?: string | number;
  userId?: string | number;
  companyId?: string | number;
  name?: string;
  deptName?: string;
  companyName?: string;
  photoPath?: string;
  /** 有手机号码表示已绑定过小程序 */
  phone?: string;
}

export interface MiniMenuItem {
  menuId?: string | number;
  menuName?: string;
  lnkIcon?: string;
  component?: string;
  query?: string;
  visible?: number;
  children?: MiniMenuItem[];
  /** 排序号 */
  orderNum?: number | null;
  /** 页面路径（部分菜单为空，使用 component 跳转） */
  path?: string;
  /** 菜单类型 */
  menuType?: string;
}

export interface StatData {
  name?: string;
  value?: string | number;
  unit?: string;
  /** 统计卡片图标标识：1 本月考勤 2 本月请假 3 本月加班 4 剩余年假 */
  icon?: string | number;
}

export interface HomeIndexResp {
  personInfo?: PersonInfo;
  menuList?: MiniMenuItem[];
  dataList?: StatData[];
}

export interface DoorDevice {
  deviceId?: string | number;
  deviceSn?: string;
  deviceName?: string;
  firstFloor?: string | null;
  endFloor?: string | null;
  doorId?: string | number;
}

/** 门（含下属设备） */
export interface DoorGroup {
  doorId?: string | number;
  doorName?: string;
  deviceList?: DoorDevice[];
}

export interface DoorOpenRecord {
  deviceName?: string;
  operName?: string;
  openTime?: string;
  /** 0 成功 1 失败 2 未执行 */
  status?: number;
}

export interface AttendanceRecord {
  deviceSn?: string;
  /** 3 补卡 4 移动考勤 其他：考勤地点 */
  signType?: number;
  addressName?: string;
  signTime?: string;
}

export interface VisitorItem {
  modulePersonId?: string | number;
  name?: string;
  phone?: string;
  cardId?: string;
  beginTime?: string;
  endTime?: string;
  workUnit?: string;
  visitorNum?: string | number;
  visitReason?: string;
  license?: string;
  photoPath?: string;
  interviewerId?: string | number;
  interviewerName?: string;
  /** 0 正常（已通过）1 待审核 2 审核不通过 */
  status?: string | number;
  /** 已选通行门 id，逗号分隔 */
  doorIds?: string | null;
}

/** 可通行门权限 */
export interface VisitDoor {
  doorId?: string | number;
  doorName?: string;
}

export interface DictItem {
  dictLabel?: string;
  dictValue?: string;
  listClass?: string;
}

export interface VisitorSaveForm {
  cid?: string | number;
  companyId?: string | number;
  deptId?: string | number;
  pid?: string | number;
  interviewerId?: string | number;
  interviewerName?: string;
  name: string;
  phone: string;
  cardId?: string;
  beginTime: string;
  endTime: string;
  visitReason: string;
  workUnit: string;
  visitorNum: string | number;
  license?: string;
  photoPath: string;
  doorIds?: string | null;
  doorName?: string;
}

/** 首页聚合数据 */
export const getMiniHome = () => axios.get<HomeIndexResp>(Api.index);
/** 工作台菜单列表 */
export const getMiniMenuList = () => axios.get<MiniMenuItem[]>(Api.menuList);
/** 获取小程序用户信息（无手机号时需跳转绑定页） */
export const getMiniInfo = () => axios.get<PersonInfo>(Api.info);
/** 获取某月考勤有记录的日期 */
export const getAttendanceDateList = (params: { attendanceMonth: string }) =>
  axios.get<string[]>(Api.attendanceDateList, params);
/** 获取某一天的考勤记录 */
export const getAttendanceRecord = (params: { attendanceDate: string }) =>
  axios.get<AttendanceRecord[]>(Api.attendanceRecord, params);
/** 远程开门门列表（门 -> 设备 两级结构） */
export const getDoorDeviceList = () => axios.get<DoorGroup[]>(Api.doorDeviceList);
/** 远程开门 */
export const openDoor = (data: { deviceSn: string }) =>
  axios.post(Api.doorOpen, data);
/** 开门记录列表 */
export const getDoorOpenList = () =>
  axios.get<DoorOpenRecord[]>(Api.doorOpenList);
/** 访客列表（name 为搜索的访客姓名） */
export const getVisitorList = (params: { name?: string }) =>
  axios.get<VisitorItem[]>(Api.visitorList, params);
/** 访客详情 */
export const getVisitorInfo = (params: { modulePersonId: string | number }) =>
  axios.get<VisitorItem>(Api.visitorInfo, params);
/** 访客审核：status 0 通过（需 doorIds）2 不通过 */
export const checkVisitor = (data: {
  modulePersonId: string | number;
  status: string;
  doorIds: string;
}) => axios.post(Api.visitorCheck, data);
/** 登记访客（自行添加，无需审核） */
export const saveVisitor = (data: VisitorSaveForm) =>
  axios.post(Api.visitorSave, data);
/** 访客通过邀请链接登记（被邀请者填写，无需登录态） */
export const openAddVisitor = (data: VisitorSaveForm) =>
  axios.post(Api.openAddVisitor, data, {
    headers: { isToken: false },
  });
/** 门列表；传 allowVisitPass=Y 时仅返回允许访客通行的门 */
export const getVisitDoorList = (params: { allowVisitPass?: string } = {}) =>
  axios.get<VisitDoor[]>(Api.visitDoorList, params);
/** 数据字典 */
export const getDictData = (dictType: string) =>
  axios.get<DictItem[]>(`/wework/dict/data/type/${dictType}`);
